import React, { useState, useEffect, useCallback } from 'react';
import {
  GOOGLE_REVIEWS,
  GOOGLE_REVIEWS_META,
  EMPTY_REVIEW_TEXT,
  REVIEW_TEXT_CLAMP,
} from '../data/googleReviews';
import './GoogleReviewsCarousel.css';

const AVATAR_COLORS = ['#1a73e8', '#34a853', '#ea4335', '#fbbc04', '#9334e6', '#00acc1'];

const StarRow = ({ size = 'md', className = '' }) => (
  <div className={`google-reviews-stars google-reviews-stars--${size} ${className}`.trim()} aria-label="5 csillag">
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} className="google-reviews-star" aria-hidden>
        ★
      </span>
    ))}
  </div>
);

const getInitials = (name) => {
  const parts = name.replace(/\./g, '').trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.[0] || '?').toUpperCase();
};

const getAvatarColor = (id) => {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const ReviewText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const body = (text || '').trim() || EMPTY_REVIEW_TEXT;
  const isLong = body.length > REVIEW_TEXT_CLAMP;
  const displayText = !isLong || expanded ? body : `${body.slice(0, REVIEW_TEXT_CLAMP).trim()}…`;

  return (
    <div className="google-review-text-wrap">
      <p className={`google-review-text ${body === EMPTY_REVIEW_TEXT ? 'google-review-text--placeholder' : ''}`}>
        {displayText}
      </p>
      {isLong && (
        <button
          type="button"
          className="google-review-more-btn"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Kevesebb' : 'Tovább olvasom'}
        </button>
      )}
    </div>
  );
};

const GoogleReviewsCarousel = () => {
  const reviews = GOOGLE_REVIEWS;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goTo = useCallback(
    (index) => {
      const next = ((index % reviews.length) + reviews.length) % reviews.length;
      setCurrentIndex(next);
    },
    [reviews.length]
  );

  const next = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);
  const prev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);

  useEffect(() => {
    if (isPaused || reviews.length <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % reviews.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [isPaused, reviews.length]);

  const review = reviews[currentIndex];
  const showDotNav = reviews.length <= 12;

  return (
    <section
      className="google-reviews-section"
      aria-label="Google értékelések"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="google-reviews-layout">
        <div className="google-reviews-summary-panel">
          <h3 className="google-reviews-summary-title">100+ 5-Csillagos Google Értékelés</h3>
          <div className="google-reviews-summary-box">
            <div className="google-reviews-summary-rating">
              {GOOGLE_REVIEWS_META.rating.toFixed(1)}
            </div>
            <div className="google-reviews-summary-stars">⭐⭐⭐⭐⭐</div>
            <p className="google-reviews-summary-count">{GOOGLE_REVIEWS_META.totalLabel}</p>
          </div>
          <p className="google-reviews-summary-note">Vélemények a Google-ról</p>
        </div>

        <div className="google-reviews-carousel-panel">
          <div className="google-reviews-track-outer">
            <button
              type="button"
              className="google-reviews-nav google-reviews-nav--prev"
              onClick={prev}
              aria-label="Előző értékelés"
            >
              ‹
            </button>

            <article className="google-review-card" key={review.id}>
              <header className="google-review-card-header">
                <div
                  className="google-review-avatar"
                  style={{ backgroundColor: getAvatarColor(review.id) }}
                  aria-hidden
                >
                  {getInitials(review.displayName)}
                </div>
                <div className="google-review-meta">
                  <h4 className="google-review-name">{review.displayName}</h4>
                  <div className="google-review-sub">
                    <StarRow size="sm" />
                    <span className="google-review-time">{review.relativeTime}</span>
                  </div>
                </div>
              </header>
              <div className="google-review-card-body">
                <ReviewText text={review.text} />
              </div>
            </article>

            <button
              type="button"
              className="google-reviews-nav google-reviews-nav--next"
              onClick={next}
              aria-label="Következő értékelés"
            >
              ›
            </button>
          </div>

          {showDotNav ? (
            <div className="google-reviews-dots">
              {reviews.map((r, index) => (
                <button
                  key={r.id}
                  type="button"
                  className={`google-reviews-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goTo(index)}
                  aria-label={`${index + 1}. értékelés: ${r.displayName}`}
                  aria-current={index === currentIndex ? 'true' : undefined}
                />
              ))}
            </div>
          ) : (
            <p className="google-reviews-counter">
              {currentIndex + 1} / {reviews.length}
            </p>
          )}

          <p className="google-reviews-footnote">
            A nevek védelme érdekében rövidítve jelennek meg.
          </p>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsCarousel;
